import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import type {
  CreateFilthBackendInput,
  FilthBackend,
  IntrospectFilthBackendResult,
  TestFilthBackendResult,
  UpdateFilthBackendInput,
} from "@/api/filth-backends-api"

import {
  createFilthBackend,
  deleteFilthBackend,
  introspectFilthBackend,
  listFilthBackends,
  testFilthBackend,
  updateFilthBackend,
} from "@/api/filth-backends-api"

export const FILTH_BACKENDS_KEY = ["admin", "filthBackends"] as const

const STALE_TIME = 30_000
const GC_TIME = 5 * 60 * 1000

export function useFilthBackendsQuery(enabled = true) {
  return useQuery<FilthBackend[]>({
    queryKey: FILTH_BACKENDS_KEY,
    queryFn: listFilthBackends,
    enabled,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  })
}

export function useCreateFilthBackendMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateFilthBackendInput) => createFilthBackend(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FILTH_BACKENDS_KEY })
    },
  })
}

export function useUpdateFilthBackendMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateFilthBackendInput }) =>
      updateFilthBackend(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FILTH_BACKENDS_KEY })
    },
  })
}

export function useDeleteFilthBackendMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteFilthBackend(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FILTH_BACKENDS_KEY })
    },
  })
}

export function useTestFilthBackendMutation() {
  return useMutation<TestFilthBackendResult, Error, string>({
    mutationFn: (id: string) => testFilthBackend(id),
  })
}

export function useIntrospectFilthBackendMutation() {
  return useMutation<IntrospectFilthBackendResult, Error, string>({
    mutationFn: (id: string) => introspectFilthBackend(id),
  })
}
